import React, { useState } from "react";
import { FaSearch, FaTimes } from "react-icons/fa";
import { BsChevronDown } from "react-icons/bs";
import useSearchFunctionalChecklists from "../../hooks/useSearchFunctionalChecklists";

const AssignChecklistModal = ({
  selectedChecklist = null,
  onChecklistChange = () => {},
  allowMultiple = false,
}) => {
  // Modal state
  const [checklistType, setChecklistType] = useState("");
  const [checklistTypeDropdownOpen, setChecklistTypeDropdownOpen] =
    useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);

  // Mock checklist types
  const checklistTypes = ["All", "Custom", "Generic"];

  // Fetch checklists using React Query
  const {
    data: checklistsData,
    isLoading: checklistsLoading,
    error: checklistsError,
  } = useSearchFunctionalChecklists(searchTerm);

  // Get checklists from API
  const checklists = checklistsData?.data?.content || [];

  // Filter checklists based on type
  const getFilteredChecklists = () => {
    if (!checklists.length) return [];

    let filtered = checklists;

    // Filter by type if not "All"
    if (checklistType && checklistType !== "All") {
      // Map type to API field - assuming API uses "CUSTOM" and "GENERIC"
      const typeMapping = {
        Custom: "CUSTOM",
        Generic: "GENERIC",
      };
      const apiType = typeMapping[checklistType];
      if (apiType) {
        filtered = filtered.filter((checklist) => checklist.type === apiType);
      }
    }

    return filtered.slice(0, 8); // Limit results
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setSearchDropdownOpen(value.trim().length > 0);
  };

  const handleChecklistToggle = (checklist) => {
    if (allowMultiple) {
      // Multiple selection logic (not used in ManageAdmissions)
      const selectedChecklists = Array.isArray(selectedChecklist)
        ? selectedChecklist
        : [];
      const isSelected = selectedChecklists.find((c) => c.id === checklist.id);
      if (isSelected) {
        onChecklistChange(
          selectedChecklists.filter((c) => c.id !== checklist.id)
        );
      } else {
        onChecklistChange([...selectedChecklists, checklist]);
      }
    } else {
      // Single selection logic
      const isSelected =
        selectedChecklist && selectedChecklist.id === checklist.id;
      if (isSelected) {
        onChecklistChange(null);
      } else {
        onChecklistChange(checklist);
      }
    }
  };

  const handleRemoveChecklist = () => {
    onChecklistChange(allowMultiple ? [] : null);
  };

  const handleCancel = () => {
    // Reset form but keep selected data
    setChecklistType("");
    setSearchTerm("");
    setSearchDropdownOpen(false);
    setChecklistTypeDropdownOpen(false);
  };

  const handleDone = () => {
    // Close modal using Bootstrap
    window.$("#assignChecklist").modal("hide");
    // Reset form
    handleCancel();
  };

  return (
    <div
      className="modal fade"
      tabIndex="-1"
      id="assignChecklist"
      aria-hidden="true"
      aria-labelledby="assignChecklist"
    >
      <div className="modal-dialog" style={{ width: "395px" }}>
        <div className="modal-content border-0 overlay-rounded">
          <div className="modal-header mt-14 border-0">
            <div className="flex-grow-1 text-center">
              <h5 className="text_primary fs-18 font-weight-bold">
                Assign Checklist
              </h5>
            </div>
            <FaTimes
              size={15}
              className="cursor-pointer text_primary"
              data-dismiss="modal"
              aria-label="Close"
              onClick={handleCancel}
            />
          </div>
          <div className="modal-body px-3">
            {/* Checklist Type Dropdown */}
            <div className="form-group mb-4">
              <label className="small-text font-weight-bold mb-2">
                Checklist Type:
              </label>
              <div className="dropdown">
                <button
                  className="btn btn-block border text-left overlay-dropdown-input small-text"
                  type="button"
                  onClick={() =>
                    setChecklistTypeDropdownOpen(!checklistTypeDropdownOpen)
                  }
                >
                  {checklistType || "Choose Checklist Type"}
                  <BsChevronDown
                    size={12}
                    style={{
                      transform: checklistTypeDropdownOpen
                        ? "rotate(180deg)"
                        : "rotate(0)",
                      transition: "transform 0.3s ease",
                      float: "right",
                      marginTop: "3px",
                    }}
                  />
                </button>
                <div
                  className={`dropdown-menu w-100 ${
                    checklistTypeDropdownOpen ? "show" : ""
                  }`}
                >
                  {checklistTypes.map((type, index) => (
                    <a
                      key={index}
                      className="dropdown-item small-text"
                      href="#"
                      onClick={() => {
                        setChecklistType(type);
                        setChecklistTypeDropdownOpen(false);
                      }}
                    >
                      {type}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Search Checklists */}
            <div className="form-group mb-4">
              <label className="small-text font-weight-bold mb-2">
                Search Checklists:
              </label>
              <div className="dropdown">
                <div className="position-relative">
                  <FaSearch
                    size={12}
                    style={{
                      position: "absolute",
                      left: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#6c757d",
                      zIndex: 2,
                    }}
                  />
                  <input
                    type="text"
                    className="btn btn-block border text-left overlay-dropdown-input small-text"
                    placeholder="Type to search checklists..."
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    style={{
                      paddingLeft: "35px",
                      paddingRight: "12px",
                    }}
                  />
                </div>

                {/* Search Results Dropdown */}
                <div
                  className={`dropdown-menu w-100 ${
                    searchDropdownOpen ? "show" : ""
                  }`}
                >
                  <p className="pl-3 mb-0 pb-2 font-weight-bold fs-13">
                    Available Checklists:
                  </p>
                  {checklistsLoading ? (
                    <div className="dropdown-item small-text text-muted">
                      Loading checklists...
                    </div>
                  ) : checklistsError ? (
                    <div className="dropdown-item small-text text-danger">
                      Error loading checklists
                    </div>
                  ) : getFilteredChecklists().length > 0 ? (
                    getFilteredChecklists().map((checklist) => (
                      <div
                        key={checklist.id}
                        className="dropdown-item d-flex align-items-center small-text"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleChecklistToggle(checklist)}
                      >
                        <input
                          type={allowMultiple ? "checkbox" : "radio"}
                          checked={
                            allowMultiple
                              ? Array.isArray(selectedChecklist) &&
                                selectedChecklist.some(
                                  (c) => c.id === checklist.id
                                )
                              : selectedChecklist &&
                                selectedChecklist.id === checklist.id
                          }
                          onChange={() => handleChecklistToggle(checklist)}
                          className="mr-3"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="flex-grow-1">
                          <div className="font-weight-bold">
                            {checklist.name}
                          </div>
                          <small className="text-muted">
                            {checklist.type} • {checklist.status}
                          </small>
                        </div>
                      </div>
                    ))
                  ) : searchTerm.trim() ? (
                    <div className="dropdown-item small-text text-muted">
                      No checklists found matching "{searchTerm}"
                    </div>
                  ) : (
                    <div className="dropdown-item small-text text-muted">
                      Type to search checklists
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Selected Checklist Display */}
            {((allowMultiple &&
              Array.isArray(selectedChecklist) &&
              selectedChecklist.length > 0) ||
              (!allowMultiple && selectedChecklist)) && (
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="small-text font-weight-bold mb-0">
                    {allowMultiple
                      ? "Selected Checklists:"
                      : "Selected Checklist:"}
                  </h6>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={handleRemoveChecklist}
                  >
                    Clear {allowMultiple ? "All" : ""}
                  </button>
                </div>
                <div className="border rounded p-3">
                  <div className="d-flex flex-wrap">
                    {allowMultiple ? (
                      Array.isArray(selectedChecklist) &&
                      selectedChecklist.map((checklist) => (
                        <span
                          key={checklist.id}
                          className="badge badge-primary mr-2 mb-2 p-2 d-flex align-items-center"
                          style={{
                            fontSize: "12px",
                            backgroundColor: "#50a193",
                          }}
                        >
                          {checklist.name}
                          <FaTimes
                            size={10}
                            className="ml-2 cursor-pointer"
                            onClick={() => {
                              const updatedChecklists =
                                selectedChecklist.filter(
                                  (c) => c.id !== checklist.id
                                );
                              onChecklistChange(updatedChecklists);
                            }}
                          />
                        </span>
                      ))
                    ) : (
                      <span
                        className="badge badge-primary mr-2 mb-2 p-2 d-flex align-items-center"
                        style={{ fontSize: "12px", backgroundColor: "#50a193" }}
                      >
                        {selectedChecklist.name}
                        <FaTimes
                          size={10}
                          className="ml-2 cursor-pointer"
                          onClick={handleRemoveChecklist}
                        />
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer Buttons - Cancel and Done */}
          <div className="modal-footer d-flex justify-content-between border-0">
            <button
              type="button"
              className="btn btn_outline_danger overlay-btn"
              data-dismiss="modal"
              onClick={handleCancel}
            >
              CANCEL
            </button>
            <button
              type="button"
              className="btn btn-primary overlay-btn"
              onClick={handleDone}
            >
              DONE
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignChecklistModal;
